import { Store, NoteService, BookmarkService } from "@viewer/core";
import { Component, Input, OnInit, OnDestroy, ChangeDetectorRef } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { MatSidenav } from "@angular/material/sidenav";
import { Router, NavigationEnd } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { SettingsFormComponent } from "@viewer/settings-form/settings-form.component";
import { RoleService } from "@viewer/core/role/role.service";
import { PrintService } from "@viewer/core/print/print.service";
import { Subscription } from "rxjs";
import { SynchroModalComponent } from "@viewer/synchro-modal/synchro-modal.component";
import { reaction, IReactionDisposer } from "mobx";
import { PymModalComponent } from "@viewer/pym-modal/pym-modal.component";
import { InfoDialogComponent } from "@viewer/shared-module/info-dialog/info-dialog.component";
import { BookmarkEditComponent } from "@viewer/toc-items/bookmark-module/bookmark-edit/bookmark-edit.component";
import { MessageService } from "@orion2/message-service/message.service";
import { MaintFolderModalComponent } from "@viewer/toc-items/maint-folder-module/maint-folder-modal/maint-folder-modal.component";
import { CommentLocation } from "@orion2/models/tocitem.models";
import { CommentService } from "@viewer/toc-items/comments-module/services/comment.service";
import { MaintFolderService } from "@viewer/toc-items/maint-folder-module/services/maint-folder.service";
import { PreprintService } from "@viewer/core/toc-items/preprint.service";

@Component({
  standalone: false,
  selector: "o-settings",
  templateUrl: "./settings.component.html",
  styleUrls: ["./settings.component.scss"]
})
export class SettingsComponent implements OnInit, OnDestroy {
  @Input() settingsSidenav: MatSidenav;

  public isBookmarked: boolean;
  public canBeBookmarked: boolean;
  public canDoMaintFolder: boolean;
  public canHaveNotes: boolean;
  public canHaveComments: boolean;
  public canBePym: boolean;
  public makeBookmarks: boolean;
  public makePrivateNote: boolean;
  public makePym: boolean;
  public commentLocation = CommentLocation;
  private userReactionDisposer: IReactionDisposer;
  private subscriptions = new Subscription();

  constructor(
    public store: Store,
    public commentService: CommentService,
    private dialog: MatDialog,
    private router: Router,
    public noteService: NoteService,
    private bookmarkService: BookmarkService,
    private translate: TranslateService,
    private messageService: MessageService,
    private printService: PrintService,
    private cd: ChangeDetectorRef,
    private roleService: RoleService,
    public maintFolderService: MaintFolderService,
    private preprintService: PreprintService
  ) {}

  ngOnInit(): void {
    this.setPageFeatures();
    /* get router events to set the features available on this page */
    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.setPageFeatures();
      }
    });

    this.userReactionDisposer = reaction(
      () => this.store.user,
      () => {
        this.makeBookmarks = this.store.user.makeBookmark;
        this.makePrivateNote = this.store.user.makePrivateNote;
        this.makePym = this.store.user.makePym;
        this.cd.markForCheck();
      },
      { fireImmediately: true }
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    if (this.userReactionDisposer) {
      this.userReactionDisposer();
    }
  }

  public close() {
    this.settingsSidenav.close();
  }

  /**
   * Opens the info popup
   */
  public openInfoDialog(): void {
    if (!this.store.duStatus || !this.canBeBookmarked) {
      return;
    }
    this.close();
    this.dialog.open(InfoDialogComponent, {
      panelClass: "info-dialog-container",
      data: { content: this.store.duStatus.outerHTML }
    });
  }

  public openSettings() {
    this.close();
    this.dialog.open(SettingsFormComponent, {
      panelClass: "settings-dialog-container"
    });
  }

  /**
   * Close Kebab menu before creating a note
   *
   * @memberof SettingsComponent
   */
  public createNote(): void {
    if (!this.canHaveNotes || !!this.noteService.editingNote()) {
      return;
    }
    this.preprintService.changeViewToSeeDmItems();
    this.close();
    this.noteService.createNote();
    this.store.isNotesPanelOpen = true;
  }

  public createComment(location: CommentLocation): void {
    this.close();
    // need to initComment before navigation to avoid getItemsOfType concurency
    this.commentService.initComment(location).then(() => {
      if (location !== CommentLocation.DM) {
        this.router.navigate([
          "pub",
          this.store.pubInfo.packageId,
          this.store.pubInfo.revision,
          "comments"
        ]);
      } else {
        this.preprintService.changeViewToSeeDmItems();
        this.store.isCommentsPanelOpen = true;
      }
    });
  }

  public createBookmarks(): void {
    const dialog = this.dialog.open(BookmarkEditComponent, {
      data: {
        mode: "add"
      },
      panelClass: "bookmark-edit"
    });
    dialog.afterClosed().subscribe((result: boolean) => {
      if (result) {
        this.isBookmarked = true;
        this.messageService.success(this.translate.instant("tocItem.saved"));
      } else {
        this.messageService.warning(this.translate.instant("bookmarks.edit.canceled"));
      }
    });
  }

  public print(): void {
    this.settingsSidenav.close().then(() => {
      this.printService.printFinal();
    });
  }

  public synchronize(): void {
    if (!this.store.isLoggedIn || this.store.deteriorate) {
      return;
    }
    this.close();
    this.dialog.open(SynchroModalComponent, {
      panelClass: "settings-dialog-container",
      disableClose: true
    });
  }

  public login(): Promise<void> {
    return this.roleService
      .login()
      .then(() => {
        this.cd.detectChanges();
      })
      .catch(error => console.error("Login error : ", error));
  }

  public logout(): Promise<boolean> {
    return this.roleService.logout().catch(error => {
      console.error("Logout error : ", error);
      return false;
    });
  }

  public openPymModal(): void {
    if (!this.canBePym || this.dialog.openDialogs.length) {
      return;
    }
    this.dialog.open(PymModalComponent, {
      panelClass: "pym-container",
      data: {
        source: [{ dmc: this.store.currentDMC }],
        filename: this.store.currentDMC
      }
    });
  }

  public openMaintFolderModal(): void {
    if (
      !this.canDoMaintFolder ||
      !this.maintFolderService.referencesMap?.[this.store.currentDMC] ||
      this.dialog.openDialogs.length
    ) {
      return;
    }
    this.dialog.open(MaintFolderModalComponent, {
      panelClass: "maint-folder-container",
      data: this.store.currentDMC
    });
  }

  private setPageFeatures(): void {
    const url = this.router.url;
    // extract dmc from url
    const dmc = url.match(/\/du\/([^\/]+)/)?.[1];
    this.canHaveNotes = dmc && dmc !== "loap";
    this.canHaveComments = dmc && dmc !== "loap" && !/preprint|sb|document/.test(dmc);
    this.canBeBookmarked = dmc && dmc !== "loap";
    this.canBePym = /fulltext|pdf|ipc/.test(url);
    if (this.canBeBookmarked) {
      this.bookmarkService.isBookmarked(dmc).then((res: boolean) => {
        this.isBookmarked = res;
      });
    }
    // SPEC: the button "generate maintenance folder" is visible if the capability is activated
    // the button is disabled out of dmc or if the dmc to not contain references (the LOAP never contains references)
    // so if the document references_map is not in DB, the button is always disabled
    this.canDoMaintFolder = dmc && dmc !== "loap";
  }
}
